import { useContext, useEffect, useState } from "react";
import trans, { convertMillisecondsToTime, isRTL } from "../../../utils";
import { exportContext } from "../../../context/ExportDataContext";
import { useLazyGetStopReportQuery } from "../../../Redux/service/NewReports/Reports";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useLazyGetStopReportSumQuery } from "../../../Redux/service/Summary/Summary";
import AntdTable from "../../antd/Table/Table";
import axios from "axios";
import { DistanceUnit } from "../../../JsHelpers/PlatformUnits";
import { useSelector } from "react-redux";
import { useReportTableColumns } from "../ReportColumns/reportColumns";

const StopSheetTable = ({
  selectedids,
  startDate,
  endDate,
  isShown,
  currentPage,
  setCurrentPage,
  setTotalData,
  sumIds,
}) => {
  const { setFileName, setExportIds, setStartDateE, setEndDateE } =
    useContext(exportContext);
  const {
    Sort,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
    limit,
    setLimit,
  } = useContext(filtrationcontext);
  const inf = useSelector((state) => state.users.user);
  const [sort, setSort] = useState({
    by: "",
    order: "",
  });

  const [trigger, { data }] = useLazyGetStopReportQuery();
  const [SumTrigger, { data: sumData }] = useLazyGetStopReportSumQuery();

  const [loading, setLoading] = useState(false);
  const [geoCodes, setGeoCode] = useState({});

  const truncateItem = (text) => {
    const maxLength = 25;
    return text?.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const fetchGeoCodePosition = async (lat, lon, id) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse.php?lat=${lat}&lon=${lon}&zoom=18&format=jsonv2`
      );
      setGeoCode((prev) => ({
        ...prev,
        [id]: response.data.display_name,
      }));
    } catch (error) {
      console.error("Error fetching geocode position:", error);
    }
  };

  const columns = [
    {
      title: trans("Vehicle"),
      dataIndex: "deviceName",
      width: 150,
      sorter: true,
      fixed: !isRTL ? "left" : undefined,
      render: (deviceName) => (
        <span className={"table-element-name ps-0"}>{deviceName}</span>
      ),
    },
    {
      title: trans("Group"),
      dataIndex: "groupName",
      sorter: true,
      fixed: !isRTL ? "left" : undefined,
      width: 150,
      render: (groupName) => groupName || trans("Ungrouped Vehicles"),
    },
    {
      title: trans("Start Time"),
      dataIndex: "startTime",
      width: 180,
      sorter: true,
      render: (startTime) => {
        const date = new Date(startTime);
        return (
          <span className="table-element-date">
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </span>
        );
      },
    },
    {
      title: trans("End Time"),
      dataIndex: "endTime",
      width: 180,
      sorter: true,
      render: (endTime) => {
        const date = new Date(endTime);
        return (
          <span className="table-element-date">
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </span>
        );
      },
    },
    {
      title: trans("Duration"),
      dataIndex: "duration",
      width: 120,
      sorter: true,
      render: (value) => convertMillisecondsToTime(value),
    },
    {
      title: trans("Odometer"),
      dataIndex: "endOdometer",
      sorter: true,
      width: 150,
      render: (value) => DistanceUnit(value,inf?.attributes?.distanceUnit || "km") ,
    },
    {
      title: trans("Address"),
      key: "address",
      width: 250,
      render: (id, record) => {
        const geoAddress = geoCodes[id];
        return (
          <span
            className="text-primary cursor"
            onClick={() =>
              fetchGeoCodePosition(record.latitude, record.longitude, id)
            }
            title={geoAddress}
          >
            {geoAddress
              ? truncateItem(geoAddress)
              : trans("Get current address")}
          </span>
        );
      },
    },
  ];
  const visibleColumns = useReportTableColumns(columns);

  useEffect(() => {
    if (isShown && startDate && endDate && selectedids.length > 0) {
      const from = new Date(startDate).toISOString().replace(/:/g, "%3A");
      const to = new Date(endDate).toISOString().replace(/:/g, "%3A");
      setLoading(true);
      SumTrigger(
        {
          from,
          to,
          selectedids: sumIds,
          page: 1,
          pageSize: 5,
        },
        true
      );

      trigger(
        {
          from,
          to,
          selectedids,
          page: currentPage,
          pageSize: limit,
          sortBy: sort.by,
          sortOrder: sort.order,
          search: searchQuery,
        },
        true
      ).finally(() => setLoading(false));
    }
  }, [
    isShown,
    startDate,
    endDate,
    selectedids,
    currentPage,
    limit,
    trigger,
    sort.by,
    sort.order,
    searchQuery,
  ]);

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      localStorage.setItem("page", 1);
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    if (sumData) {
      setTotalData([
        {
          title: "Total Vehicles Tracked",
          number: sumData?.summary.deviceCount,
          type: "vehicles",
        },
        {
          title: "Total Stops",
          number: sumData?.summary.totalStops,
          type: "count",
        },
        {
          title: "Average Stop Duration",
          number: sumData?.summary.averageStopDuration,
          type: "timeT",
        },
        {
          title: "Total Stop Duration",
          number: sumData?.summary.totalStopDuration,
          type: "timeT",
        },
      ]);
    }
  }, [data, sumData]);

  useEffect(() => {
    if (data) {
      setFileName("Stop Report");
      setExportIds(selectedids);
      setStartDateE(startDate);
      setEndDateE(endDate);
    }
  }, [data, startDate, endDate, selectedids]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }
  };

  return (
    <div className="bg-white-rounded-top mt-3 table-parent">
      <AntdTable
        columns={visibleColumns}
        dataSource={data?.data || []}
        loading={loading}
        pageSize={limit}
        totalItems={data?.totalCount}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setCurrentPage(1);
        }}
        onTableChange={handleTableChange}
        scrollY={350}
      />
    </div>
  );
};

export default StopSheetTable;
